import React, { MouseEventHandler } from 'react';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';

export interface WorkflowOption {
  value: string;
  label: string;
  icon?: JSX.Element;
  customStyles?: React.CSSProperties;
  onClick: MouseEventHandler<HTMLElement>;
}

interface ValidateWorkFlowOptionsParams {
  t: (key: string) => string;
  masterPoolAccountStatusCode: string;
  checkForApprovalDateRules: (code: string) => boolean;
  checkForWithdrawRules: (code: string) => boolean;
  reverseApproval: () => void;
  openWithdrawalDateModal: () => void;
  checkForReverseFinalizationRules: (code: string) => boolean;
  checkForReverseWithdrawRules: (code: string) => boolean;
  openReverseFinalizationModal: () => void;
  openReverseWithdrawModal: () => void;
  handleCloseMenu: () => void;
  styles: Record<string, string>;
}

export const createOption = (
  value: string,
  label: string,
  iconTypeOrElement: string | JSX.Element,
  onClick: () => void,
  styles: Record<string, string>,
  testID?: string,
  isRemarkIcon?: boolean
): WorkflowOption => {
  const iconClassName = isRemarkIcon ? styles.iconRemark : styles.iconMargin;

  return {
    value,
    label,
    onClick,
    icon:
      typeof iconTypeOrElement === 'string' ? (
        <CmhcIcon
          className={iconClassName}
          size={CmhcIconSize.MEDIUM}
          type={iconTypeOrElement}
          testID={testID}
        />
      ) : (
        <span className={iconClassName}>{iconTypeOrElement}</span>
      )
  };
};

export const validateWorkFlowOptions = ({
  t,
  masterPoolAccountStatusCode,
  checkForApprovalDateRules,
  checkForWithdrawRules,
  reverseApproval,
  checkForReverseFinalizationRules,
  checkForReverseWithdrawRules,
  openReverseFinalizationModal,
  openWithdrawalDateModal,
  openReverseWithdrawModal,
  handleCloseMenu,
  styles
}: ValidateWorkFlowOptionsParams): WorkflowOption[] => {
  const options: WorkflowOption[] = [];

  const addOption = (condition: boolean, option: WorkflowOption) => {
    if (condition) options.push(option);
  };

  addOption(
    checkForApprovalDateRules(masterPoolAccountStatusCode),
    createOption(
      'reverseApproval',
      t('Reverse-Approval'),
      'icon_undo',
      () => {
        reverseApproval();
        handleCloseMenu();
      },
      styles,
      'reverseApprovalTestId'
    )
  );

  addOption(
    checkForWithdrawRules(masterPoolAccountStatusCode),
    createOption(
      'withDraw',
      t('Withdraw'),
      'icon_minus',
      () => {
        openWithdrawalDateModal();
        handleCloseMenu();
      },
      styles,
      'withdraw-icon'
    )
  );

  addOption(
    checkForReverseFinalizationRules(masterPoolAccountStatusCode),
    createOption(
      'reverseFinalization',
      t('Reverse-Reverse-Finalization'),
      'icon_undo',
      () => {
        openReverseFinalizationModal();
        handleCloseMenu();
      },
      styles,
      'reverseFinalization-icon'
    )
  );

  addOption(
    checkForReverseWithdrawRules(masterPoolAccountStatusCode),
    createOption(
      'reverseWithdraw',
      t('Reverse-Withdrawl'),
      'icon_undo',
      () => {
        openReverseWithdrawModal();
        handleCloseMenu();
      },
      styles,
      'reverseWithdraw-icon'
    )
  );

  return options;
};
